import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './AudioRecorder.css';
import LiveTranscriptionRecorder from './LiveTranscriptionRecorder';
import { useTranscriptionLimit } from '../hooks/useTranscriptionLimit';
import { useAuth } from '../hooks/useAuth';
import Icon from './Icons';
import analytics from '../utils/analytics';
import { debugLog } from '../utils/debug';

// Audio waveform visualization component
const AudioWaveform = ({ level = 0 }) => {
  const barCount = 7;
  const minHeight = 4;
  const maxHeight = 16;

  return (
    <div className="audio-waveform">
      {Array.from({ length: barCount }).map((_, i) => {
        // Create variation based on position (middle bars taller)
        const positionFactor = 1 - Math.abs(i - (barCount - 1) / 2) / ((barCount - 1) / 2);
        const height = minHeight + (maxHeight - minHeight) * level * (0.5 + 0.5 * positionFactor);
        return (
          <div
            key={i}
            className="waveform-bar"
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
};

const AudioRecorder = ({ onRecordingComplete, onRecordingStart, onRecordingCancel, onRecordingStop, onDownload, onDownloadTxt, hasTranscript, isAuthenticated = false, onLoginPrompt, onLiveTranscriptUpdate, onLiveSessionComplete, onFileUpload, autoRecord = false, userTier = null, onLiveModeChange }) => {
  // Use provided userTier prop if available (for guest mode), otherwise get from auth
  const authContext = useAuth();
  const effectiveUserTier = userTier !== null ? userTier : authContext?.user?.tier;
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState(null);
  const [isLiveMode, setIsLiveModeState] = useState(false);
  const [selectedMode, setSelectedMode] = useState('record'); // 'record' or 'live'

  // Wrapper to notify parent when live mode changes
  const setIsLiveMode = useCallback((value) => {
    setIsLiveModeState(value);
    if (onLiveModeChange) onLiveModeChange(value);
  }, [onLiveModeChange]);
  const [dragOver, setDragOver] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const { checkLimit, LimitAlert } = useTranscriptionLimit();

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoRecordTriggeredRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Check browser support
  useEffect(() => {
    const checkSupport = () => {
      // Check for MediaRecorder API
      if (!window.MediaRecorder) {
        setIsSupported(false);
        setError('MediaRecorder API is not supported in this browser. Please update your browser.');
        return;
      }

      setIsSupported(true);
    };

    checkSupport();
  }, []);

  // Timer for recording duration
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);

  // Warn user when trying to leave page during recording
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers show this message
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Monitor audio volume levels
  const monitorVolume = useCallback(() => {
    if (!analyserRef.current) {
      console.warn('[AudioRecorder] monitorVolume called but analyser is not available');
      return;
    }

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    debugLog('[AudioRecorder] Starting volume monitoring loop, frequencyBinCount:', analyser.frequencyBinCount);

    let frameCount = 0;
    const updateVolume = () => {
      analyser.getByteFrequencyData(dataArray);

      // Calculate RMS (root mean square) for better volume representation
      const sumSquares = dataArray.reduce((acc, val) => acc + (val * val), 0);
      const rms = Math.sqrt(sumSquares / dataArray.length);

      // Apply gain and scaling to make the indicator more responsive
      // Typical speaking voice RMS is around 20-40, so we scale and boost
      const gain = 3.0; // Boost the signal
      const volume = Math.min(100, (rms / 255) * 100 * gain);

      setVolumeLevel(volume);

      // Volume level monitoring (logging disabled for production)
      frameCount++;

      // Continue monitoring if still recording
      if (animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateVolume);
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateVolume);
  }, []);

  // Clean up audio analysis when recording stops
  const stopVolumeMonitoring = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolumeLevel(0);
  }, []);

  const startRecording = useCallback(async () => {
    // Recording is free for everyone - login required only when accessing text
    try {
      setError(null);

      // Skip limit check for unauthenticated users (they'll be prompted at transcription)
      const canProceed = isAuthenticated ? await checkLimit() : true;
      if (!canProceed) {
        return;
      }

      // Track when unauthenticated users start recording
      if (!isAuthenticated) {
        analytics.trackEvent('guest_recording_started', {
          category: 'engagement',
          action: 'record_without_login',
          page: window.location.pathname
        });
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      chunksRef.current = [];

      // Set up audio analysis for volume monitoring
      try {
        debugLog('[AudioRecorder] Setting up volume monitoring...');
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.1; // Very responsive
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        debugLog('[AudioRecorder] Volume monitoring setup complete, starting monitoring loop');
        // Start monitoring volume
        monitorVolume();
      } catch (err) {
        console.error('[AudioRecorder] Failed to set up volume monitoring:', err);
        // Continue recording even if volume monitoring fails
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm'
        });
        setAudioBlob(blob);

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Track when unauthenticated users complete recording
        if (!isAuthenticated) {
          const durationSeconds = Math.round((Date.now() - (recordingStartTimeRef.current || Date.now())) / 1000);
          analytics.trackEvent('guest_recording_completed', {
            category: 'engagement',
            action: 'recording_completed_without_login',
            duration_seconds: durationSeconds,
            file_size_kb: Math.round(blob.size / 1024),
            page: window.location.pathname
          });
        }

        if (onRecordingComplete) {
          // Create a File object for compatibility with existing upload handler
          const timestamp = recordingStartTimeRef.current || Date.now();
          debugLog('[AudioRecorder] Recording stopped, passing timestamp to callback:', timestamp);
          const file = new File([blob], `recording.webm`, {
            type: blob.type
          });
          // Pass both file and timestamp to the callback
          onRecordingComplete(file, timestamp);
        }
        
        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      // Store the timestamp when recording starts
      recordingStartTimeRef.current = Date.now();

      if (onRecordingStart) {
        onRecordingStart();
      }
    } catch (err) {
      console.error('Error starting recording:', err);

      // Clean up on error
      stopVolumeMonitoring();

      // Provide specific error messages based on the error type
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone access denied. Please allow microphone permissions and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Microphone is already in use by another application.');
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        setError('Microphone does not meet the required specifications.');
      } else if (err.name === 'NotSupportedError') {
        setError('Audio recording is not supported on this device.');
      } else if (err.name === 'AbortError') {
        setError('Recording was aborted due to a hardware error.');
      } else {
        setError(`Failed to start recording: ${err.message || 'Unknown error'}. Please check microphone permissions.`);
      }
    }
  }, [onRecordingComplete, onRecordingStart, isAuthenticated, monitorVolume, stopVolumeMonitoring, checkLimit]);

  // Auto-start recording when autoRecord prop is true (e.g., from share card "Try Rec-Chord")
  useEffect(() => {
    if (autoRecord && !autoRecordTriggeredRef.current && isSupported && !isRecording && !audioBlob) {
      autoRecordTriggeredRef.current = true;
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        startRecording();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoRecord, isSupported, isRecording, audioBlob, startRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  }, [isRecording, isPaused]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      // Show processing indicator immediately
      setIsProcessing(true);

      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      // Stop volume monitoring
      stopVolumeMonitoring();

      if (onRecordingStop) {
        onRecordingStop();
      }
    }
  }, [isRecording, onRecordingStop, stopVolumeMonitoring]);

  const downloadRecording = useCallback(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [audioBlob]);

  const clearRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
  }, [audioUrl]);

  // File upload handlers
  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (file && onFileUpload) {
      onFileUpload(file);
    }
    // Clear the input value so the same file can be selected again
    event.target.value = '';
  }, [onFileUpload]);

  const handleFileInputClick = useCallback(async (event) => {
    // File upload requires login (only recording is free)
    if (!isAuthenticated) {
      event.preventDefault();
      // Track unauthenticated upload attempt
      analytics.trackEvent('unauthenticated_action', {
        category: 'conversion',
        action_type: 'upload_attempt',
        page: window.location.pathname
      });
      if (onLoginPrompt) {
        onLoginPrompt();
      }
      return;
    }

    const canProceed = await checkLimit();
    if (!canProceed) {
      event.preventDefault();
    }
  }, [isAuthenticated, onLoginPrompt, checkLimit]);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    setDragOver(false);

    // File upload requires login (only recording is free)
    if (!isAuthenticated) {
      // Track unauthenticated drop attempt
      analytics.trackEvent('unauthenticated_action', {
        category: 'conversion',
        action_type: 'drop_attempt',
        page: window.location.pathname
      });
      if (onLoginPrompt) {
        onLoginPrompt();
      }
      return;
    }

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const canProceed = await checkLimit();
      if (!canProceed) {
        return;
      }

      const file = files[0];
      if (onFileUpload) {
        onFileUpload(file);
      }
    }
  }, [onFileUpload, isAuthenticated, onLoginPrompt, checkLimit]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  if (!isSupported) {
    return (
      <div className="audio-recorder error">
        <div className="recorder-message">
          <Icon name="microphone" size={48} className="text-gray-400" />
          <p>{error}</p>
          <div className="browser-help">
            <small>Supported browsers:</small>
            <ul>
              <li>Chrome 47+</li>
              <li>Firefox 29+</li>
              <li>Safari 14.1+</li>
              <li>Edge 79+</li>
            </ul>
            <small>Note: HTTPS is required for microphone access (except on localhost)</small>
            <button 
              onClick={() => {
                debugLog('Manual API test:');
                debugLog('navigator.mediaDevices:', navigator.mediaDevices);
                debugLog('getUserMedia:', navigator.mediaDevices?.getUserMedia);
                debugLog('MediaRecorder:', window.MediaRecorder);
                debugLog('MediaRecorder.isTypeSupported:', MediaRecorder?.isTypeSupported);
                debugLog('MediaRecorder supports webm:', MediaRecorder?.isTypeSupported?.('audio/webm'));
                navigator.mediaDevices?.getUserMedia?.({ audio: true })
                  .then(stream => {
                    debugLog('✅ getUserMedia test successful:', stream);
                    stream.getTracks().forEach(track => track.stop());
                  })
                  .catch(err => console.error('❌ getUserMedia test failed:', err));
              }}
              style={{ marginTop: '10px', padding: '5px 10px', fontSize: '12px' }}
            >
              🔧 Test APIs Manually
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if live transcription is enabled (environment + Premium tier required)
  const isLiveTranscriptionEnabled = process.env.REACT_APP_ENABLE_LIVE_TRANSCRIPTION === 'true';

  return (
    <div
      className={`audio-recorder ${onFileUpload && !isRecording && !audioBlob && !isLiveMode ? 'droppable' : ''} ${dragOver ? 'drag-over' : ''}`}
      onDrop={onFileUpload && !isRecording && !audioBlob && !isLiveMode ? handleDrop : undefined}
      onDragOver={onFileUpload && !isRecording && !audioBlob && !isLiveMode ? handleDragOver : undefined}
      onDragLeave={onFileUpload && !isRecording && !audioBlob && !isLiveMode ? handleDragLeave : undefined}
      onClick={onFileUpload && !isRecording && !audioBlob && !isLiveMode ? async (e) => {
        // Only trigger file dialog if NOT clicking on a button, input, or recorder controls
        if (e.target.tagName !== 'BUTTON' &&
            e.target.tagName !== 'INPUT' &&
            !e.target.closest('button') &&
            !e.target.closest('.record-button-container') &&
            !e.target.closest('.recorder-controls') &&
            !e.target.closest('.main-controls')) {
          // File upload requires login
          if (!isAuthenticated) {
            if (onLoginPrompt) {
              onLoginPrompt();
            }
            return;
          }

          const canProceed = await checkLimit();
          if (canProceed) {
            fileInputRef.current?.click();
          }
        }
      } : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,.wav,.ogg,.m4a,.aac,.flac,.wma,.opus,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.3gp,.ogv,.mpg,.mpeg,.ts,.vob"
        onChange={handleFileChange}
        onClick={handleFileInputClick}
        style={{ display: 'none' }}
      />
      <div className="recorder-header">
        {audioBlob && !isLiveMode && (
          <div className="status-indicator">
            <Icon name="check" size={20} />
          </div>
        )}
      </div>

      {isLiveMode ? (
        <LiveTranscriptionRecorder
          onTranscriptUpdate={onLiveTranscriptUpdate}
          onSessionComplete={(transcript, segments) => {
            setIsLiveMode(false);
            if (onLiveSessionComplete) onLiveSessionComplete(transcript, segments);
          }}
          onError={(err) => setError(err.message)}
          isAuthenticated={isAuthenticated}
          onLoginPrompt={onLoginPrompt}
          autoStart={true}
        />
      ) : (
        <>
          {/* Standard recorder UI */}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="recorder-controls">
        {!isRecording && !audioBlob && onFileUpload && isAuthenticated && (
          <div className="upload-hint-compact">
            <span className="upload-icon-small">📁</span>
            <span className="upload-text-compact">Click or drag file to upload</span>
          </div>
        )}

        {isRecording && (
          <div className="recording-status-bar">
            <div className="status-left">
              <span className={`recording-indicator ${!isPaused ? 'active' : ''}`}></span>
              <span className="recording-status-text">
                {isPaused ? 'Paused' : 'Recording'}
              </span>
              <span className="time">{formatTime(recordingTime)}</span>
              {!isPaused && (
                <div className="volume-leds">
                  {[20, 40, 60, 80, 100].map((threshold, i) => (
                    <span
                      key={i}
                      className={`volume-led ${volumeLevel >= threshold ? 'active' : ''} ${i >= 3 ? 'high' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="main-controls">
          {!isRecording && !audioBlob && (
            <>
              {isLiveTranscriptionEnabled && (
                <div className="mode-toggle" onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`mode-toggle-option ${selectedMode === 'record' ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedMode('record'); }}
                  >
                    <Icon name="microphone" size={16} />
                    Record
                  </button>
                  <button
                    className={`mode-toggle-option ${selectedMode === 'live' ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedMode('live'); }}
                  >
                    <Icon name="bolt" size={16} />
                    Live
                  </button>
                </div>
              )}

              <div className="record-button-container">
                <button
                  className="control-btn record-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedMode === 'live') {
                      setIsLiveMode(true);
                    } else {
                      startRecording();
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedMode === 'live') {
                      setIsLiveMode(true);
                    } else {
                      startRecording();
                    }
                  }}
                  title={selectedMode === 'live' ? 'Start Live Transcription' : 'Start Recording'}
                  aria-label={selectedMode === 'live' ? 'Start Live Transcription' : 'Start Recording'}
                >
                </button>
              </div>
            </>
          )}

          {isRecording && (
            <>
              <button
                className="control-btn pause-btn"
                onClick={pauseRecording}
                title={isPaused ? 'Resume Recording' : 'Pause Recording'}
              >
                <Icon name={isPaused ? 'play' : 'pause'} size={24} />
              </button>

              <button
                className="control-btn stop-btn"
                onClick={stopRecording}
                title="Stop Recording"
              >
                <Icon name="stop" size={24} />
              </button>
            </>
          )}

          {audioBlob && !isRecording && (
            <>
              <button
                className="control-btn record-btn"
                onClick={startRecording}
                onContextMenu={(e) => {
                  e.preventDefault();
                  startRecording();
                }}
                title="Record Again (Left-click or Right-click)"
                aria-label="Record Again"
              >
              </button>

              <button
                className="control-btn clear-btn"
                onClick={clearRecording}
                title="Clear Recording"
              >
                <Icon name="delete" size={24} />
              </button>

              <button
                className="control-btn download-btn"
                onClick={downloadRecording}
                title="Download Recording"
              >
                <Icon name="download" size={24} />
              </button>

              {hasTranscript && onDownload && (
                <button
                  className="control-btn download-btn-json"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDownload();
                  }}
                  title="Download JSON"
                >
                  📄
                </button>
              )}

              {hasTranscript && onDownloadTxt && (
                <button
                  className="control-btn download-btn-txt"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDownloadTxt();
                  }}
                  title="Download TXT"
                >
                  <Icon name="document" size={24} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {audioUrl && (
        <div className="playback-section">
          <div className="playback-header">
            <h4>Recording Preview</h4>
          </div>
          
          <audio 
            controls 
            src={audioUrl}
            className="audio-preview"
          >
            Your browser does not support audio playback.
          </audio>
        </div>
      )}
        </>
      )}

      {/* Processing Overlay - rendered via portal to ensure full screen coverage */}
      {isProcessing && createPortal(
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner"></div>
            <h3>Processing Recording</h3>
            <p>Preparing your audio for transcription...</p>
            <div className="processing-steps">
              <div className="processing-step active">
                <span className="step-dot"></span>
                <span>Converting audio</span>
              </div>
              <div className="processing-step">
                <span className="step-dot"></span>
                <span>Uploading to server</span>
              </div>
              <div className="processing-step">
                <span className="step-dot"></span>
                <span>Starting transcription</span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <LimitAlert />
    </div>
  );
};

export default AudioRecorder;
